/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faBolt, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";
import { CustomColumnTypes, UpdateType } from "@/enum/list.enum";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useGetColumnTemplatesWithAiSnippetQuery } from "@/pages/Private/redux/list/list.api";
import { useAppSelector } from "@/redux/hooks";

import { ColumnConfig } from "../../../List/schema/columnConfig";

import { NEW_SNIPPET_DRAFT } from "./defaultPrompts";
import { useAiSnippetForm } from "./hooks/useAiSnippetForm";
import { useSnippetPersistence } from "./hooks/useSnippetPersistence";
import { AiSnippetSchema, MAX_FEW_SHOT_EXAMPLES, SNIPPET_NAME_PATTERN } from "./schema/aiSnippet";
import { buildFewShotFromLeads } from "./utils/leadFewShot";
import {
	buildColumnTokenRewriter,
	buildSnippetToken,
	extractInsertedColumnIds,
} from "./utils/snippetTokens";
import { SnippetListPane } from "./components/SnippetListPane";
import { SnippetPromptPane } from "./components/SnippetPromptPane";
import { SnippetConfigPane } from "./components/SnippetConfigPane";
import { SnippetModalFooter } from "./components/SnippetModalFooter";
import { UnsavedChangesGuard } from "./components/UnsavedChangesGuard";

interface AiSnippetsModalProps {
	isOpen: boolean;
	listId: number;
	allSnippets: ColumnConfig[];
	initialColumn?: ColumnConfig | null;
	currentMessage?: string;
	currentPremiumMessage?: string;
	onClose: () => void;
	onInsertToken: (token: string) => boolean;
	/**
	 * Called when a list-column's name changes during an Update so the parent
	 * can rewrite any stale `{column_<id>_<oldName>|<oldName>}` references
	 * inside the current message bodies. Returning the rewritten strings keeps
	 * the message and the column in sync (so the campaign doesn't become
	 * invalid with a stale token).
	 */
	onColumnRenamed?: (columnId: number, newName: string, rewrite: (text: string) => string) => void;
}

export const AiSnippetsModal: FunctionComponent<AiSnippetsModalProps> = ({
	isOpen,
	listId,
	allSnippets,
	initialColumn,
	currentMessage,
	currentPremiumMessage,
	onClose,
	onInsertToken,
	onColumnRenamed,
}) => {
	const { t } = useTranslation();
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);

	const { isSaving, persist } = useSnippetPersistence(listId);
	const { data: templatesResponse } = useGetColumnTemplatesWithAiSnippetQuery({
		page: 1,
		limit: 1000,
	});

	const {
		values,
		mode,
		isDirty,
		selectedColumn,
		selectColumn,
		loadColumn,
		startNew,
		discard,
		setName,
		setPrompt,
		setPromptType,
		setAllowedValues,
		setFewShotExamples,
	} = useAiSnippetForm(initialColumn);

	const [showUnsavedGuard, setShowUnsavedGuard] = useState<boolean>(false);

	// Re-sync the form every time the modal opens, or when the parent swaps
	// `initialColumn` for a different snippet while the modal is open.
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (initialColumn) {
			loadColumn(initialColumn);
		} else {
			startNew({ prompt: NEW_SNIPPET_DRAFT });
		}
	}, [isOpen, initialColumn]);

	// The library list shown in the left pane combines list-column snippets
	// already used in this list with company-wide templates.
	const libraryItems = useMemo<ColumnConfig[]>(() => {
		const items: ColumnConfig[] = [...allSnippets];

		const ids = new Set(allSnippets.map((c) => c.id));

		for (const template of templatesResponse?.data ?? []) {
			if (template.id != null && !ids.has(template.id)) {
				items.push(template);
			}
		}

		return items;
	}, [allSnippets, templatesResponse]);

	const insertedColumnIds = useMemo(
		() => extractInsertedColumnIds([currentMessage ?? "", currentPremiumMessage ?? ""]),
		[currentMessage, currentPremiumMessage]
	);

	const trimmedName = values.name.trim();

	const nameError = useMemo<string | undefined>(() => {
		if (!trimmedName) {
			return undefined;
		}

		if (!SNIPPET_NAME_PATTERN.test(trimmedName)) {
			return t("aiSnippet.modal.name.invalid");
		}

		const typedLower = trimmedName.toLowerCase();

		// List-columns and company-wide templates are separate namespaces — a
		// list-column "pain point" and a template "pain point" can coexist. So:
		// - when editing a list-column we only check other list-columns,
		// - when editing a template we only check other templates,
		// - when creating new we check against everything so the new entry
		//   doesn't collide regardless of which store it lands in.
		const selectedIsListColumn = selectedColumn
			? allSnippets.some((s) => s.id === selectedColumn.id)
			: false;

		const candidates: ColumnConfig[] = selectedColumn
			? selectedIsListColumn
				? allSnippets
				: (templatesResponse?.data ?? [])
			: libraryItems;

		const duplicate = candidates.some(
			(item) => item.id !== selectedColumn?.id && item.name?.toLowerCase().trim() === typedLower
		);

		if (duplicate) {
			return t("aiSnippet.modal.name.duplicate");
		}

		return undefined;
	}, [trimmedName, allSnippets, templatesResponse, libraryItems, selectedColumn, t]);

	const validation = AiSnippetSchema.safeParse({
		name: trimmedName,
		prompt: values.prompt,
		promptType: values.promptType,
		allowedValues: values.allowedValues,
		fewShotExamples: values.fewShotExamples,
	});

	const canSubmit =
		validation.success && !nameError && values.fewShotExamples.length <= MAX_FEW_SHOT_EXAMPLES;

	const buildPayload = useCallback(
		(opts: {
			columnId?: number;
			saveAsTemplate: boolean;
			customColumnName?: string;
			templateId?: number;
		}) => ({
			customColumnType: CustomColumnTypes.AI_SNIPPET,
			customColumnName: opts.customColumnName ?? trimmedName,
			online: false,
			saveAsTemplate: opts.saveAsTemplate,
			templateId: opts.templateId,
			value: {
				prompt: values.prompt,
				promptType: values.promptType,
				allowedValues:
					values.promptType === AiSnippetEnum.SCHEMA
						? values.allowedValues.filter((v) => v.trim() !== "")
						: [],
				fewShotExamples: values.fewShotExamples.filter(
					(e) => e.leadContext.trim() !== "" && e.idealOutput.trim() !== ""
				),
			},
			updateType: UpdateType.CUSTOM_COLUMN,
			prospectIds: inbox[currentLeadIndex]?.id ? [inbox[currentLeadIndex].id] : [],
			id: listId,
			columnId: opts.columnId,
		}),
		[trimmedName, values, listId, inbox, currentLeadIndex]
	);

	const persistAndInsert = useCallback(
		async (payload: any) => {
			const result = await persist(payload);

			if (!result.ok) {
				return;
			}

			// Every caller of `persistAndInsert` creates a *new* column, so the
			// token must use the id the server just assigned. Snippet placeholders
			// are resolved by column id, so a non-numeric fallback (or a template
			// id) would never resolve at send time — if we can't determine the real
			// id, close without inserting a broken token rather than emit one.
			const columnIdForToken = result.updatedColumn?.id ?? payload.columnId;

			if (columnIdForToken == null) {
				console.error("Could not resolve the saved AI snippet column id; skipping insert");
				onClose();

				return;
			}

			onInsertToken(buildSnippetToken(columnIdForToken, payload.customColumnName));
			onClose();
		},
		[persist, onInsertToken, onClose]
	);

	const handleUpdate = useCallback(async () => {
		if (!canSubmit || !selectedColumn?.id) {
			return;
		}

		const previousName = selectedColumn.name ?? "";
		const newName = trimmedName;
		const renamed = previousName.trim() !== newName;

		const result = await persist(
			buildPayload({ columnId: selectedColumn.id, saveAsTemplate: false })
		);

		if (!result.ok) {
			return;
		}

		if (renamed && selectedColumn.id != null) {
			onColumnRenamed?.(
				selectedColumn.id,
				newName,
				buildColumnTokenRewriter(selectedColumn.id, newName)
			);
		}

		onClose();
	}, [
		canSubmit,
		selectedColumn?.id,
		selectedColumn?.name,
		trimmedName,
		persist,
		buildPayload,
		onClose,
		onColumnRenamed,
	]);

	const handleSaveAsNew = useCallback(() => {
		if (!canSubmit) {
			return;
		}

		const baseName = trimmedName;
		const collidesWithExisting = libraryItems.some(
			(item) => item.name?.toLowerCase().trim() === baseName.toLowerCase()
		);

		const nameForNew = collidesWithExisting ? `${baseName} (copy)` : baseName;

		persistAndInsert(buildPayload({ saveAsTemplate: true, customColumnName: nameForNew }));
	}, [canSubmit, trimmedName, libraryItems, persistAndInsert, buildPayload]);

	const handleInsertEphemeral = useCallback(() => {
		if (!canSubmit) {
			return;
		}

		persistAndInsert(buildPayload({ saveAsTemplate: false }));
	}, [canSubmit, persistAndInsert, buildPayload]);

	const handleSaveAsTemplateAndInsert = useCallback(() => {
		if (!canSubmit) {
			return;
		}

		persistAndInsert(buildPayload({ saveAsTemplate: true }));
	}, [canSubmit, persistAndInsert, buildPayload]);

	// Insert an already-saved snippet selected from the library, unchanged.
	const handleInsertExisting = useCallback(() => {
		if (!selectedColumn?.id) {
			return;
		}

		// List-columns already live on this list, so their token resolves as-is.
		// Company-wide templates do not yet — persist one as a list column first
		// (via the same create flow as a brand-new snippet) so the inserted token
		// references a real column id.
		const isListColumn = allSnippets.some((s) => s.id === selectedColumn.id);

		if (isListColumn) {
			onInsertToken(buildSnippetToken(selectedColumn.id, selectedColumn.name ?? trimmedName));
			onClose();

			return;
		}

		persistAndInsert(buildPayload({ saveAsTemplate: false, templateId: selectedColumn.id }));
	}, [
		selectedColumn?.id,
		selectedColumn?.name,
		allSnippets,
		onInsertToken,
		onClose,
		trimmedName,
		persistAndInsert,
		buildPayload,
	]);

	const handlePrefillFromLeads = useCallback(() => {
		const examples = buildFewShotFromLeads(inbox ?? [], values.prompt ?? "");

		if (examples.length === 0) {
			return;
		}

		const existingMap = new Map<string, boolean>();

		values.fewShotExamples.forEach((e) => {
			existingMap.set(`${e.leadContext}::${e.idealOutput}`, true);
		});

		const additions = examples.filter(
			(e) => !existingMap.has(`${e.leadContext}::${e.idealOutput}`)
		);

		setFewShotExamples([...values.fewShotExamples, ...additions].slice(0, MAX_FEW_SHOT_EXAMPLES));
	}, [inbox, values.prompt, values.fewShotExamples, setFewShotExamples]);

	const canPrefillFewShotFromLeads = useMemo(
		() =>
			Boolean(
				values.prompt &&
				(inbox?.length ?? 0) > 0 &&
				values.fewShotExamples.length < MAX_FEW_SHOT_EXAMPLES
			),
		[values.prompt, inbox, values.fewShotExamples.length]
	);

	const handleCloseRequest = useCallback(() => {
		if (
			mode === "modified" ||
			(mode === "new" && (values.name || values.prompt !== NEW_SNIPPET_DRAFT))
		) {
			setShowUnsavedGuard(true);

			return;
		}

		onClose();
	}, [mode, values.name, values.prompt, onClose]);

	const handleConfirmClose = useCallback(() => {
		setShowUnsavedGuard(false);
		onClose();
	}, [onClose]);

	// Keyboard: Esc closes (with unsaved-changes guard).
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (showUnsavedGuard) {
					setShowUnsavedGuard(false);
				} else {
					handleCloseRequest();
				}
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [isOpen, showUnsavedGuard, handleCloseRequest]);

	if (!isOpen) {
		return null;
	}

	const overlay = (
		<div
			className="fixed inset-0 z-[2000] flex items-center justify-center bg-[rgba(17,24,39,0.45)] backdrop-blur-[2px]"
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					handleCloseRequest();
				}
			}}
		>
			<div className="relative w-[min(1280px,96vw)] h-[min(760px,92vh)] bg-white rounded-2xl shadow-[0_30px_60px_rgba(0,0,0,0.35)] flex flex-col overflow-hidden">
				<div className="px-6 py-4.5 border-b border-border-light flex items-center justify-between flex-shrink-0">
					<div className="flex flex-col gap-0.5">
						<div className="text-[17px] font-semibold text-text inline-flex items-center gap-2">
							<span className="w-[26px] h-[26px] rounded-lg bg-blue-action-200 text-blue-action flex items-center justify-center">
								<Icon className="w-3.5 h-3.5" icon={faBolt} />
							</span>
							{t("aiSnippet.modal.title")}
						</div>
						<div className="text-xs text-text-meta ml-[34px]">{t("aiSnippet.modal.subtitle")}</div>
					</div>
					<button
						aria-label={t("aiSnippet.modal.close")}
						className="w-8 h-8 border-0 bg-transparent rounded-md cursor-pointer text-text-meta hover:bg-background-light hover:text-text flex items-center justify-center"
						type="button"
						onClick={handleCloseRequest}
					>
						<Icon className="w-4 h-4" icon={faXmark} />
					</button>
				</div>

				<div className="flex-1 flex overflow-hidden min-h-0">
					<SnippetListPane
						draftName={values.name}
						insertedColumnIds={insertedColumnIds}
						isCreatingNew={mode === "new"}
						isDirty={isDirty}
						selectedColumnId={selectedColumn?.id}
						snippets={libraryItems}
						onCreateNew={() => startNew({ prompt: NEW_SNIPPET_DRAFT })}
						onSelect={selectColumn}
					/>
					<SnippetPromptPane
						autoFocusName={mode === "new"}
						editorKey={selectedColumn?.id != null ? `col-${selectedColumn.id}` : "new"}
						name={values.name}
						nameError={nameError}
						prompt={values.prompt}
						onChangeName={setName}
						onChangePrompt={setPrompt}
					/>
					<SnippetConfigPane
						allowedValues={values.allowedValues}
						canPrefillFewShotFromLeads={canPrefillFewShotFromLeads}
						fewShotExamples={values.fewShotExamples}
						promptType={values.promptType}
						onChangeAllowedValues={setAllowedValues}
						onChangeFewShotExamples={setFewShotExamples}
						onChangePromptType={setPromptType}
						onPrefillFewShotFromLeads={handlePrefillFromLeads}
					/>
				</div>

				<SnippetModalFooter
					canSubmit={canSubmit}
					isLoading={isSaving}
					mode={mode}
					onCancel={handleCloseRequest}
					onDiscardChanges={discard}
					onInsertEphemeral={handleInsertEphemeral}
					onInsertExisting={handleInsertExisting}
					onSaveAsNew={handleSaveAsNew}
					onSaveAsTemplateAndInsert={handleSaveAsTemplateAndInsert}
					onUpdate={handleUpdate}
				/>
			</div>

			{showUnsavedGuard && (
				<UnsavedChangesGuard
					onCancel={() => setShowUnsavedGuard(false)}
					onConfirm={handleConfirmClose}
				/>
			)}
		</div>
	);

	return ReactDOM.createPortal(overlay, document.body);
};
